import { getSupabaseClient } from './supabase';
import { publicSupabase } from '../lib/supabaseClient';
import type { SignatureModalValue } from '../components/signature/SignatureModal';

export type AgreementDocumentKind = 'quote' | 'completion' | 'agreement';
export type AgreementQuote = {
  quote_id: string;
  version: number;
  family_id: string;
  client_id: string;
  client_branch_id: number | null;
  location_id: string;
  tier_id: string;
  price: number;
  term_years: number;
  start_date: string;
  terms: string;
  status: string;
  updated_at: string;
  snapshot: Record<string, unknown>;
  approvals?: AgreementQuoteApproval[];
};
// One approval step on a quote (service_agreement_quote_approvals). band_id is null for the
// override step submit adds when no Service agreements policy covers the quote.
export type AgreementQuoteApproval = {
  approval_id: string;
  quote_id: string;
  band_id: string | null;
  approver_profile_id: string | null;
  approver_role: string | null;
  status: string;
  decided_by: string | null;
  decided_at: string | null;
  reason: string | null;
  band: { approver_label: string | null; policy: { name: string | null } | null } | null;
};
export type AgreementQuoteDetail = AgreementQuote & {
  approvals: AgreementQuoteApproval[];
  client: { business_name: string | null } | null;
};
export type PendingAgreementApproval = AgreementQuoteApproval & {
  quote: {
    quote_id: string;
    version: number;
    price: number;
    updated_at: string;
    tier_name: string | null;
    client: { business_name: string | null } | null;
  };
};
export type AgreementDocument = {
  kind: AgreementDocumentKind;
  id: string;
  title: string;
  status: string;
  client_id?: string;
  client_name?: string;
  recipient_email?: string;
  version?: number;
  price?: number;
  term_years?: number;
  start_date?: string;
  end_date?: string;
  warranty_start_date?: string;
  warranty_end_date?: string;
  terms?: string;
  scope?: string;
  address?: string;
  benefits?: Record<string, unknown>;
  visits?: Array<{
    id: string;
    sequence: number;
    due_date: string;
    status: string;
    allocated: boolean;
    completed_at: string | null;
  }>;
  signature?: {
    signer_name: string;
    signed_at: string;
    method: string;
    image?: string;
    record_hash: string;
    statement: string;
    signed_payload: AgreementDocument;
  };
};
export async function agreementRpc<T>(
  name: string,
  args: Record<string, unknown> = {},
  guest = false,
): Promise<T> {
  const { data, error } = await (guest ? publicSupabase : getSupabaseClient()).rpc(name, args);
  if (error) throw error;
  return data as T;
}
const APPROVAL_COLUMNS =
  'approval_id,quote_id,band_id,approver_profile_id,approver_role,status,decided_by,decided_at,reason,band:approval_bands(approver_label,policy:approval_policies(name))';
export async function fetchAgreementQuotes(clientId: string): Promise<AgreementQuote[]> {
  const { data, error } = await getSupabaseClient()
    .from('service_agreement_quotes')
    .select(`*,approvals:service_agreement_quote_approvals(${APPROVAL_COLUMNS})`)
    .eq('client_id', clientId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as AgreementQuote[];
}
export async function fetchAgreementQuote(quoteId: string): Promise<AgreementQuoteDetail | null> {
  const { data, error } = await getSupabaseClient()
    .from('service_agreement_quotes')
    .select(
      `*,client:clients(business_name),approvals:service_agreement_quote_approvals(${APPROVAL_COLUMNS})`,
    )
    .eq('quote_id', quoteId)
    .maybeSingle();
  if (error) throw error;
  return (data as AgreementQuoteDetail | null) ?? null;
}
// Pending steps on quotes awaiting approval that could be the viewer's: the override step, the
// viewer's own step, or a role step. The caller narrows role steps to the viewer's roles.
export async function fetchPendingAgreementApprovals(
  profileId: string,
): Promise<PendingAgreementApproval[]> {
  const { data, error } = await getSupabaseClient()
    .from('service_agreement_quote_approvals')
    .select(
      `${APPROVAL_COLUMNS},quote:service_agreement_quotes!inner(quote_id,version,price,updated_at,tier_name:snapshot->>name,client:clients(business_name))`,
    )
    .eq('status', 'pending')
    .eq('quote.status', 'approval_pending')
    .or(`band_id.is.null,approver_profile_id.eq.${profileId},approver_role.not.is.null`)
    .limit(100);
  if (error) throw error;
  return (data ?? []) as unknown as PendingAgreementApproval[];
}
export const agreementQuoteAction = (
  action: string,
  id: string | null,
  values: Record<string, unknown> = {},
) =>
  agreementRpc<string>('service_agreement_quote_action', {
    p_action: action,
    p_quote_id: id,
    p_values: values,
  });
export const fetchAgreementDocument = (kind: AgreementDocumentKind, id?: string, token?: string) =>
  agreementRpc<AgreementDocument>(
    'service_agreement_document',
    { p_kind: kind, p_target_id: id ?? null, p_token: token ?? null },
    !!token,
  );
export const signAgreementDocument = (
  kind: AgreementDocumentKind,
  id: string,
  signature: SignatureModalValue,
  email: string,
  token?: string,
) =>
  agreementRpc<string>(
    'accept_service_agreement_document',
    { p_kind: kind, p_target_id: id, p_signature: { ...signature, email }, p_token: token ?? null },
    !!token,
  );
export async function createAgreementLink(kind: AgreementDocumentKind, id: string, email: string) {
  const token = await agreementRpc<string>('service_agreement_create_link', {
    p_kind: kind,
    p_target_id: id,
    p_email: email,
  });
  return `${window.location.origin}/service-agreements/sign/${encodeURIComponent(token)}`;
}
